import type { Vector3 } from "@babylonjs/core/Maths/math.vector";

import type { FbmOptions } from "./noise";
import { fbm2D } from "./noise";

export interface TerrainOptions extends FbmOptions {
  /**
   * Amplitude maximale, en unités monde. L'altitude reste **garantie** dans
   * `[-amplitude, +amplitude]` : le relief ne peut pas dépasser cette borne.
   */
  readonly amplitude: number;
  /**
   * Longueur d'onde de la première octave, en unités monde. C'est le réglage
   * qui borne la *raideur* : l'amplitude dit à quelle hauteur monte une colline,
   * la longueur d'onde dit en combien de mètres elle y arrive.
   */
  readonly wavelength: number;
}

export const DEFAULT_TERRAIN_OPTIONS: TerrainOptions = {
  seed: 1337,
  // Creux à crête : 10 unités, soit dix fois la hauteur du cube. En deçà, le
  // relief ne se lit plus à distance — il faut que les collines aient une taille
  // comparable à ce que la portée du regard embrasse.
  amplitude: 5,
  // Deux à trois collines dans le champ de la caméra, qui embrasse une centaine
  // d'unités. Une longueur d'onde bien plus grande donnerait un terrain
  // techniquement vallonné mais visuellement plat : on n'en verrait jamais
  // qu'un versant, et rien ne distinguerait le relief d'un plan incliné.
  wavelength: 64,
  // Trois octaves : la plus fine fait alors 16 unités de longueur d'onde, soit
  // huit quads de maillage. Une quatrième descendrait à quatre quads, un détail
  // que le sol ne saurait plus décrire.
  octaves: 3,
  lacunarity: 2,
  persistence: 0.5,
};

/**
 * Pas des différences centrées, en unités monde, pour `normalAt`.
 *
 * Trop petit, la normale suivrait le moindre grain du bruit ; trop grand, elle
 * lisserait les creux. Une unité — l'arête du cube — donne l'inclinaison que
 * « ressent » un objet de cette taille, ce qui est exactement ce qu'on veut pour
 * le poser sur la pente.
 */
const NORMAL_EPSILON = 1;

/**
 * Champ de hauteur infini.
 *
 * L'altitude est une fonction pure des coordonnées monde : deux morceaux de sol
 * voisins, générés à des instants différents, tombent nécessairement sur la même
 * valeur le long de leur arête commune. C'est ce qui rend le pavage en chunks
 * possible sans aucune passe de raccord.
 */
export class Terrain {
  readonly options: TerrainOptions;

  /** Inverse de la longueur d'onde, pré-calculé : `heightAt` est très sollicité. */
  private readonly frequency: number;

  constructor(options: Partial<TerrainOptions> = {}) {
    this.options = { ...DEFAULT_TERRAIN_OPTIONS, ...options };
    this.frequency = 1 / this.options.wavelength;
  }

  /**
   * Altitude du sol en `(x, z)`. Toujours dans `[-amplitude, +amplitude]`.
   *
   * La pente, elle, est bornée par le *rapport* amplitude / longueur d'onde. La
   * dérivée de la courbe quintique culmine à 1,875 et deux sommets voisins
   * diffèrent d'au plus 2, d'où `|∂h/∂x| ≤ amplitude × 3,75 / wavelength ×
   * Σ(persistence × lacunarity)ⁱ / Σ persistenceⁱ`. Avec les réglages par défaut
   * cela donne 0,33 par axe — **18° au pire**, 25° en diagonale — et cinq à six
   * degrés en pratique : praticable pour un cube d'une unité lancé à 6 u/s.
   *
   * Le facteur `persistence × lacunarity` vaut 1 par défaut : chaque octave
   * apporte alors la *même* pente maximale, et la borne croît linéairement avec
   * leur nombre. Ajouter du détail sans raidir le terrain demande donc de
   * descendre la persistance sous `1 / lacunarity`.
   */
  heightAt(x: number, z: number): number {
    return fbm2D(x * this.frequency, z * this.frequency, this.options) * this.options.amplitude;
  }

  /**
   * Normale de la surface, écrite dans `result` (rendu pour l'enchaînement).
   *
   * Calculée **analytiquement**, par différences centrées sur le champ de
   * hauteur, et non par moyenne des faces d'un mesh : la valeur ne dépend alors
   * que des coordonnées monde. Deux chunks voisins trouvent donc rigoureusement
   * la même normale sur leur arête commune, là où `VertexData.ComputeNormals`,
   * qui ne connaît que les triangles du chunk courant, laisserait une couture
   * d'éclairage nette à chaque frontière.
   */
  normalAt(x: number, z: number, result: Vector3, epsilon: number = NORMAL_EPSILON): Vector3 {
    const dhdx = (this.heightAt(x + epsilon, z) - this.heightAt(x - epsilon, z)) / (2 * epsilon);
    const dhdz = (this.heightAt(x, z + epsilon) - this.heightAt(x, z - epsilon)) / (2 * epsilon);

    // Pour une surface `y = h(x, z)`, la normale est `(-∂h/∂x, 1, -∂h/∂z)`.
    // Sa composante `y` reste donc strictement positive : la normale ne peut
    // jamais basculer, quelle que soit la pente.
    return result.set(-dhdx, 1, -dhdz).normalize();
  }
}
